import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from '../../../../shared/services/utils/utils.service';
import { UserPreferencesService } from '../../../../shared/services/user-preferences/user-preferences.service';
import { Constants, UploadOperation } from '../../../../shared/constants/common';
import { GlobalService } from '../../../../shared/services/global/global-service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { HttpClient } from '@angular/common/http';
import { UploadMediaRequest } from '../../../../shared/models/template.model';
import { UserPreferencesData } from '../../../../shared/models/user-preferences.model';

interface MediaInfo {
  caption: string;
  data: string;
  uploadDate: string;
  uploadedByName: string;
  url: string;
}

interface ChecklistItem {
  media?: MediaInfo[];
  val?: string | string[] | null;
  note?: string;
  action?: unknown[];
}

interface ItemInfo {
  formattedItemCode: string;
  itemCode: string;
  caption?: string;
}

@Component({
    selector: 'app-create-media',
    templateUrl: './create-media.component.html',
    styleUrl: './create-media.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-37/COL-36), out of scope here
    standalone: false
})
export class CreateMediaComponent implements OnInit {
  private utilsService = inject(UtilsService);
  dialogRef = inject<MatDialogRef<CreateMediaComponent>>(MatDialogRef);
  private userPreferencesService = inject(UserPreferencesService);
  private globalService = inject(GlobalService);
  private toastrService = inject(ToastrService);
  private loaderService = inject(LoaderService);
  private http = inject(HttpClient);
  data = inject<{
    data;
}>(MAT_DIALOG_DATA);

  userPreferencesData: UserPreferencesData;
  checklists: Record<string, ChecklistItem>;
  itemInfo: ItemInfo;
  templateType: string;
  recordId: number;
  sectionNo: number | null;
  isChangesMade = false;

  constructor() {
    const data = this.data;
 
    this.itemInfo = data["itemInfo"];
    this.checklists = data["checklists"];
    this.templateType = data["templateType"];
    this.recordId = data["recordId"];
    this.sectionNo = data["sectionNo"];
  }

  async ngOnInit(): Promise<void> {
    this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
  }

  closeDialog() {
    this.dialogRef.close({ shouldReload: this.isChangesMade });
  }

  onSubmit(): void {
    this.closeDialog();
  }

  onSelectMedia(event: Event) {
    const selectedFiles = (event.target as HTMLInputElement).files;
    if (selectedFiles) {
      this.handleFiles(selectedFiles);
      this.utilsService.refreshModuleChanges.next(true);
    }
  }

  onDragOver(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, fileInput: HTMLInputElement) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files) {
      const dataTransfer = new DataTransfer();
      for (const file of Array.from(files)) {
        dataTransfer.items.add(file);
      }
      fileInput.files = dataTransfer.files;
      this.handleFiles(files);
    }
  }

  async handleFiles(files: FileList) {
    const oversizedFiles = Array.from(files).filter(file => file.size > Constants.MaxMediaFileSizeBytes);
    if (oversizedFiles.length > 0) {
      this.toastrService.error(
        `File(s) exceed the ${Constants.MaxMediaFileSizeBytes / (1024 * 1024)}MB size limit: ${oversizedFiles.map(f => f.name).join(', ')}`
      );
      return;
    }

    await Promise.all(Array.from(files).map(async file => {
      if(this.recordId > 0){
        this.uploadMedia(file);
      }
      else{
        const mediaInfo: MediaInfo = {
          caption: file.name,
          data: '',
          uploadDate: new Date().toISOString(),
          uploadedByName: this.userPreferencesData?.userName,
          url: ''
        };
        this.checklists[this.itemInfo.formattedItemCode]["media"].push(mediaInfo);
        this.isChangesMade = true;
      }
    }));
  }

  deletefile(itemInfo: ItemInfo, fileInfo: MediaInfo){
    const request: UploadMediaRequest = {
      recordId: this.recordId,
      moduleName: this.templateType,
      referenceId: '0',
      referenceTable: Constants.MediaType.Media,
      itemCode: this.itemInfo.itemCode,
      sectionNo: this.sectionNo,
      file: undefined,
      mediaKey: fileInfo.data,
      action: UploadOperation.Delete,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    this.loaderService.showLoader('Deleting...');
    this.globalService.uploadMedia(request).subscribe({
      next: (response) => {
        if (response?.success) {
          const obj = this.checklists[itemInfo.formattedItemCode].media.indexOf(fileInfo);
          this.checklists[itemInfo.formattedItemCode].media.splice(obj, 1);
          this.isChangesMade = true;
          this.toastrService.success('Media deleted successfully!');
        }
        else{
          this.toastrService.error('Failed to delete');
        }
        this.loaderService.hideLoader();
      },
      error: (err) => {
        this.loaderService.hideLoader();
        this.toastrService.error('Error while deleting');
        console.error('Error while deleting media:', err);
      }
    });
  }

  uploadMedia(file: File){
    const request: UploadMediaRequest = {
      recordId: this.recordId,
      moduleName: this.templateType,
      referenceId: '0',
      referenceTable: Constants.MediaType.Media,
      itemCode: this.itemInfo.itemCode,
      sectionNo: this.sectionNo,
      file: file,
      mediaKey: '',
      action: UploadOperation.Upload,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    if(this.checklists[this.itemInfo.formattedItemCode]['media'] == undefined){
      this.checklists[this.itemInfo.formattedItemCode]['media'] = [];
    }
    
    this.loaderService.showLoader('Uploading...');
    this.globalService.uploadMedia(request).subscribe({
      next: (response) => {
        if (response?.success) {
          const mediaInfo: MediaInfo = {
            caption: file.name,
            data: response.mediaKey,
            uploadDate: new Date().toISOString(),
            uploadedByName: this.userPreferencesData?.userName,
            url: response.imageUrl
          };
          this.checklists[this.itemInfo.formattedItemCode]['media'].push(mediaInfo);
          this.isChangesMade = true;
          this.toastrService.success('Media uploaded successfully!');
        }
        else{
          this.toastrService.error('Failed to upload');
        }
        this.loaderService.hideLoader();
      },
      error: (err) => {
        this.loaderService.hideLoader();
        this.toastrService.error('Error while uploading');
        console.error('Error while uploading media:', err);
      }
    });
  }

  downloadFile(fileInfo: MediaInfo){
    this.loaderService.showLoader();
    this.globalService.getMedia(fileInfo.data).subscribe({
       next: (response) => {
        this.http.get(response, { responseType: 'blob' }).subscribe((blob) => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = fileInfo.caption;
          a.click();
          URL.revokeObjectURL(objectUrl);
        });
        this.loaderService.hideLoader();
       },
        error: (err) => {
          this.loaderService.hideLoader();
          this.toastrService.error('Something went wrong dowloading file');
          console.error('Error while downloading file:', err);
        }
    });
  }

  viewFile(fileInfo: MediaInfo){
    this.loaderService.showLoader();
    this.globalService.getMedia(fileInfo.data).subscribe({
       next: (response) => {
        window.open(response, '_blank');
        this.loaderService.hideLoader();
       },
        error: (err) => {
          this.loaderService.hideLoader();
          this.toastrService.error('Something went wrong dowloading file');
          console.error('Error while downloading file:', err);
        }
    });
  }
}
